import type {LocalMediaKind} from '@phonehq/rtc-core';

export type DeviceIssue = 'denied' | 'unavailable';

export type DeviceIssues = Partial<Record<LocalMediaKind, DeviceIssue>>;

const PERMISSION_NAMES: Record<LocalMediaKind, string> = {microphone: 'microphone', camera: 'camera'};
const DEVICE_KINDS: Record<LocalMediaKind, MediaDeviceKind> = {microphone: 'audioinput', camera: 'videoinput'};

export const classifyMediaError = (error: unknown): DeviceIssue => {
    const name = typeof error === 'object' && error !== null ? (error as {name?: unknown}).name : undefined;
    const message = error instanceof Error ? error.message : '';
    if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError' || /permission/i.test(message)) {
        return 'denied';
    }
    return 'unavailable';
};

const isPermissionDenied = async (kind: LocalMediaKind): Promise<boolean> => {
    try {
        const status = await navigator.permissions?.query({name: PERMISSION_NAMES[kind] as PermissionName});
        return status?.state === 'denied';
    } catch {
        return false;
    }
};

const hasDevice = async (kind: LocalMediaKind): Promise<boolean> => {
    try {
        const devices = await navigator.mediaDevices?.enumerateDevices();
        return !devices || devices.some(device => device.kind === DEVICE_KINDS[kind]);
    } catch {
        return true;
    }
};

export const probeDeviceIssues = async (kinds: readonly LocalMediaKind[]): Promise<DeviceIssues> => {
    const entries = await Promise.all(kinds.map(async (kind): Promise<[LocalMediaKind, DeviceIssue | undefined]> => {
        if (await isPermissionDenied(kind)) {
            return [kind, 'denied'];
        }
        return [kind, (await hasDevice(kind)) ? undefined : 'unavailable'];
    }));
    return Object.fromEntries(entries.filter(([, issue]) => issue)) as DeviceIssues;
};

export const requestDevices = async (kinds: readonly LocalMediaKind[]): Promise<DeviceIssues> => {
    const entries = await Promise.all(kinds.map(async (kind): Promise<[LocalMediaKind, DeviceIssue | undefined]> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia(kind === 'microphone' ? {audio: true} : {video: true});
            stream.getTracks().forEach(track => track.stop());
            return [kind, undefined];
        } catch (error) {
            return [kind, classifyMediaError(error)];
        }
    }));
    return Object.fromEntries(entries.filter(([, issue]) => issue)) as DeviceIssues;
};
